// Chart principal (timelapse banda × bloque de 10 min, apilado por
// continente). Equivalente a draw() + la leyenda fija de
// continentes del original (public/cabrillo/src/plantilla.html:80,89-94).
// Ver src/cabrillo/CREDITOS.md.
//
// Mismas escalas/fórmulas/colores que el original: se porta línea por
// línea, solo reemplazando M/cur/bf/cnt/mx globales por motor.modelo /
// motor.obtenerEstado().

import { CONT, CNAME, CL } from '../constantes.js';
import { bl } from '../geo.js';
import { pad, css, observarRedimension } from './render-util.js';

/**
 * @param {{cv: HTMLCanvasElement, leg: HTMLElement}} els
 * @param {ReturnType<import('../motor.js').crearAnalizador>} motor
 */
export function montar(els, motor) {
    const ctx = els.cv.getContext('2d');

    // Leyenda de continentes: fija por concurso, no depende de cur/bf
    // (igual que en el original, se pinta una sola vez).
    els.leg.innerHTML = CONT.slice(0, 6)
        .map((c) => `<span><i style="background:${CL[c]}"></i>${CNAME[c]}</span>`)
        .join('');

    els.cv.onclick = (e) => {
        const { bands, NB } = motor.modelo;
        const b = els.cv.getBoundingClientRect();
        const i = Math.floor(((e.clientX - b.left - 44) / (b.width - 50)) * NB);
        if (i >= 0 && i < NB) {
            motor.pausar();
            motor.irA(i);
        }
    };

    function draw() {
        const { bands, NB, t0, cnt, mx } = motor.modelo;
        const { cur, bf } = motor.obtenerEstado();
        const nb = bands.length;
        const dpr = devicePixelRatio || 1;
        const W = els.cv.clientWidth;
        if (W === 0) return; // aún sin layout válido; el ResizeObserver vuelve a llamar cuando lo tenga
        const L = 44, R = 6, T = 6, BT = 34, lh = 72;
        const H = T + BT + nb * lh;

        els.cv.style.height = H + 'px';
        els.cv.width = W * dpr;
        els.cv.height = H * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const pw = W - L - R;
        const bw = pw / NB;
        const fg = css(els.cv, '--fg');
        const mut = css(els.cv, '--mut');
        const line = css(els.cv, '--line');
        ctx.font = '11px system-ui';
        ctx.textBaseline = 'middle';

        for (let b = 0; b < nb; b++) {
            const y0 = T + lh * (b + 1) - 2;
            ctx.strokeStyle = line;
            ctx.beginPath();
            ctx.moveTo(L, y0 + 0.5);
            ctx.lineTo(L + pw, y0 + 0.5);
            ctx.stroke();
            ctx.fillStyle = fg;
            ctx.textAlign = 'right';
            ctx.fillText(bl(bands[b]), L - 6, y0 - lh / 2);

            for (let i = 0; i < NB; i++) {
                let y = y0;
                ctx.globalAlpha = (bf >= 0 && b != bf ? 0.3 : 1) * (i <= cur ? 1 : 0.16);
                for (let c = 0; c < CONT.length; c++) {
                    const n = cnt[b][i][c];
                    if (!n) continue;
                    const h = (n / mx) * (lh - 8);
                    ctx.fillStyle = CL[CONT[c]];
                    ctx.fillRect(L + i * bw, y - h, Math.max(bw - 0.6, 0.8), h);
                    y -= h;
                }
            }
            ctx.globalAlpha = 1;
        }

        const st = NB > 200 ? 6 : 3;
        ctx.textBaseline = 'top';
        for (let h = 0; h <= NB / 6; h += st) {
            const x = L + h * 6 * bw;
            const d = new Date(t0 + h * 36e5);
            const hr = d.getUTCHours();
            ctx.strokeStyle = line;
            ctx.beginPath();
            ctx.moveTo(x + 0.5, T);
            ctx.lineTo(x + 0.5, H - BT);
            ctx.stroke();
            ctx.fillStyle = mut;
            ctx.textAlign = 'center';
            ctx.fillText(hr + 'h', x, H - BT + 5);
            if (hr == 0 || h == 0) {
                ctx.fillStyle = fg;
                ctx.textAlign = 'left';
                ctx.fillText(pad(d.getUTCDate()) + '/' + pad(d.getUTCMonth() + 1), x + 2, H - BT + 18);
            }
        }

        ctx.strokeStyle = css(els.cv, '--acc');
        ctx.lineWidth = 2;
        const px = L + (cur + 1) * bw;
        ctx.beginPath();
        ctx.moveTo(px, T);
        ctx.lineTo(px, H - BT);
        ctx.stroke();
        ctx.lineWidth = 1;
    }

    draw();
    const desuscribir = motor.subscribe(draw);
    const dejarDeObservar = observarRedimension(els.cv, draw);

    return {
        actualizar: draw,
        destruir() {
            desuscribir();
            dejarDeObservar();
        },
    };
}
